"use client";

/**
 * <TimezoneProvider> resolves the active timezone configuration once,
 * near the root of the app, so every <Timestamp> and chart formatter
 * can read the same numbers without re-deriving them per call.
 *
 * Resolution order, evaluated on every render of the provider:
 *
 *   1. user.timezone           (explicit personal preference)
 *   2. company.timezone        (org-level default for company users)
 *   3. GLOBAL_ADMIN_DEFAULT_TZ (only for GAs without a company)
 *   4. BROWSER_TZ              (last resort — login page, etc.)
 *
 * For company users the secondary tooltip slot is always the company
 * timezone. For global admins (no company) the secondary slot is the
 * three LATAM regional zones (Brazil / Peru / Mexico). Components can
 * override the company side via <Timestamp companyId={...} /> when a
 * GA is viewing a specific company's pages.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/lib/auth/context";
import { getCompany } from "@/lib/api/company";
import {
  PRODUCT_LOCALE,
  BROWSER_TZ,
  GLOBAL_ADMIN_DEFAULT_TZ,
} from "./format";
import { DATA_REGIONS, type RegionTimezone } from "./regions";

export interface TimezoneContextValue {
  /** The timezone every primary label uses. Always defined. */
  primary: string;
  /** Company timezone (or null when not in a company context). */
  company: string | null;
  /** The browser-detected timezone. Always defined. */
  browser: string;
  /** True when the resolved primary differs from the browser zone. */
  isMismatched: boolean;
  /** True when the user is a global admin operating outside a company. */
  isGlobalAdminContext: boolean;
  /**
   * Regional zones surfaced for global admins outside a company context.
   * Sourced from the data-region registry so adding GCC / Europe later
   * is a single edit in [regions.ts](./regions.ts).
   */
  regional: ReadonlyArray<RegionTimezone>;
  /** Resolved BCP 47 locale used for formatting. */
  locale: string;
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Company doc is only fetched when the user has a company id — global
  // admins outside a company hit zero requests here.
  const { data: company } = useQuery({
    queryKey: ["company"],
    queryFn: getCompany,
    enabled: Boolean(user?.companyId),
    staleTime: 5 * 60 * 1000,
  });

  // browser TZ is captured at module load, but recalc on mount in case
  // the user changes their system TZ mid-session (rare, but cheap).
  const [browser, setBrowser] = useState(BROWSER_TZ);
  useEffect(() => {
    if (typeof Intl === "undefined") return;
    const current = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (current && current !== browser) setBrowser(current);
  }, [browser]);

  const value = useMemo<TimezoneContextValue>(() => {
    const userTz = user?.timezone ?? null;
    const companyTz = company?.timezone ?? null;
    const isGA = Boolean(user?.isGlobalAdmin && !user.companyId);

    let primary: string;
    if (userTz) {
      primary = userTz;
    } else if (companyTz) {
      primary = companyTz;
    } else if (isGA) {
      primary = GLOBAL_ADMIN_DEFAULT_TZ;
    } else {
      primary = browser;
    }

    return {
      primary,
      company: companyTz,
      browser,
      isMismatched: primary !== browser,
      isGlobalAdminContext: isGA,
      // Today only LATAM exists. When/if GAs need to view multiple regions,
      // expand this to read from a UI-level "active region" selection.
      regional: isGA ? DATA_REGIONS.LATAM.secondaryTimezones : [],
      locale: PRODUCT_LOCALE,
    };
  }, [user?.timezone, user?.isGlobalAdmin, user?.companyId, company?.timezone, browser]);

  return (
    <TimezoneContext value={value}>{children}</TimezoneContext>
  );
}

/**
 * Hook — read the resolved timezone configuration. Throws if used
 * outside <TimezoneProvider> so missing wrap-ups fail loudly during
 * development instead of silently falling back to browser TZ.
 */
export function useTimezones(): TimezoneContextValue {
  const ctx = useContext(TimezoneContext);
  if (ctx === null) {
    throw new Error(
      "useTimezones() must be used inside <TimezoneProvider>. " +
      "Mount it once near the root of the app tree.",
    );
  }
  return ctx;
}
